/**
 * @file LaunchBanner.tsx
 * @description Плашка «Магазин ещё не открыт» над шапкой кабинета магазина
 * (phase19.28 этап А; PL8, PL9): подсказка `SITE_CLOSURE_LAUNCH_CABINET_HINT`,
 * [Смотреть сайт] и (с правом настроек) [Открыть сайт] — с проверкой
 * условий и чек-листом (`useLaunchOpen`). Вид — как у плашки паузы магазина.
 */

import { CirclePause } from "lucide-react";

import { Button } from "../ui/button";
import { useLaunchOpen } from "../../hooks/site-access/useLaunchOpen";
import type { SiteAccessHandle } from "../../hooks/site-access/useSiteAccess";
import { useSiteAccessActions } from "../../hooks/site-access/useSiteAccessActions";
import type { LaunchBannerModel } from "../../lib/site-access/site-access-view";
import { LaunchChecklistDialog } from "./LaunchChecklistDialog";

const BANNER_CLASS =
  "flex flex-wrap items-center gap-x-3 gap-y-2 bg-[var(--warning-soft)] px-4 py-2 text-on-surface [box-shadow:inset_0_-1px_0_var(--warning-line)]";

export function LaunchBanner({ model, access }: { model: LaunchBannerModel; access: SiteAccessHandle }) {
  const actions = useSiteAccessActions(access);
  const launch = useLaunchOpen(access);
  return (
    <div role="region" aria-label="Доступ к сайту" data-testid="site-closed-banner" data-kind="launch" className={BANNER_CLASS}>
      <CirclePause className="size-5 shrink-0 self-start text-warning max-md:mt-0.5 md:self-center" aria-hidden />
      <p role="status" className="type-body min-w-0 flex-1 break-words">
        {model.text}
      </p>
      <div className="flex shrink-0 gap-2 max-md:w-full">
        <Button variant="outline" className="max-md:flex-1" loading={actions.busy === "watch"} onClick={() => actions.watch()}>
          Смотреть сайт
        </Button>
        {model.canManage ? (
          <Button className="max-md:flex-1" loading={launch.busy} onClick={() => void launch.start(model.closureId)}>
            Открыть сайт
          </Button>
        ) : null}
      </div>
      <LaunchChecklistDialog launch={launch} />
    </div>
  );
}
